/**
 * Update Schedule — "In progress" section. Work under way whose REMAINING
 * DURATION re-measures against the data date (finish held). Shows old → new
 * duration per activity, WBS-grouped, with an editable New column: the default
 * is the auto-computed value; editing one overrides it; the host footer's
 * apply accepts them. These are NOT in the preview's "will move".
 */
import React from "react";
import { formatDateNumeric } from "../../../../utils/format";
import type { AutoActualsResponse } from "../../../../models";
import { groupByWbs } from "../wbsGroup";
import "./InProgressSection.css";

interface Props {
  data: AutoActualsResponse;
  /** activity_id -> overridden duration (days). */
  edits: Record<number, number>;
  onEdit: (id: number, days: number) => void;
}

const NUM_COLS = 6;

const InProgressSection: React.FC<Props> = ({ data, edits, onEdit }) => {
  const rows = React.useMemo(() => groupByWbs(data.activities), [data]);

  if (data.activities.length === 0) {
    return (
      <div className="aat">
        <div className="aat-empty">
          No in-progress work re-measures at this data date — nothing to review here.
        </div>
      </div>
    );
  }

  return (
    <div className="aat">
      <div className="aat-note">
        Work under way, re-measured to the data date — the planned <b>finish holds</b>;
        the <b>remaining days</b> recount. Edit a New value to override; otherwise the
        auto-computed value is applied.
      </div>
      <div className="aat-table-wrap">
        <table className="aat-table">
          <colgroup>
            <col className="aat-c-id" />
            <col className="aat-c-name" />
            <col className="aat-c-num" />
            <col className="aat-c-new" />
            <col className="aat-c-num" />
            <col className="aat-c-date" />
          </colgroup>
          <thead>
            <tr>
              <th>Activity ID</th>
              <th>Activity Name</th>
              <th className="aat-r">Old Rem Dur</th>
              <th className="aat-r">New Rem Dur</th>
              <th className="aat-r">Δ</th>
              <th>Finish (held)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              if (r.kind === "wbs") {
                return (
                  <tr key={r.key} className={`aat-wbs aat-depth-${Math.min(r.depth, 3)}`}>
                    <td colSpan={NUM_COLS} style={{ paddingLeft: r.depth * 18 + 8 }}>
                      <strong>{r.name}</strong>
                      <span className="aat-count">{r.count}</span>
                    </td>
                  </tr>
                );
              }
              const a = r.act;
              const eff = edits[a.activity_id] ?? a.new_duration;
              const delta = eff - a.old_duration;
              const edited = edits[a.activity_id] != null && edits[a.activity_id] !== a.new_duration;
              return (
                <tr key={r.key} className="aat-row">
                  <td className="aat-mono">{a.task_code}</td>
                  <td className="aat-name">{a.task_name}</td>
                  <td className="aat-r aat-mono aat-dim">{a.old_duration}d</td>
                  <td className="aat-r">
                    <input
                      type="number"
                      min={1}
                      step={1}
                      className={`aat-dur-input${edited ? " edited" : ""}`}
                      value={eff}
                      onChange={(e) => {
                        // Remaining is a whole number of workdays >= 1; the
                        // backend (edit_duration) rejects 0 / fractional with a 500.
                        const v = Math.round(parseFloat(e.target.value));
                        onEdit(a.activity_id, Number.isFinite(v) ? Math.max(1, v) : 1);
                      }}
                    />
                  </td>
                  <td className={`aat-r aat-mono${delta < 0 ? " aat-down" : delta > 0 ? " aat-up" : " aat-dim"}`}>
                    {delta === 0 ? "—" : `${delta > 0 ? "+" : ""}${delta}d`}
                  </td>
                  <td className="aat-mono aat-dim">{formatDateNumeric(a.finish)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InProgressSection;
